"use client";

import { useAnalytics } from "@/lib/analytics";
import { faqItems } from "@/lib/faq";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@meetploy/storybook";

/** Maps an accordion value (`item-3`) back to its index in `faqItems`. */
function faqIndexFromValue(value: unknown): number | null {
	if (typeof value !== "string") {
		return null;
	}
	const index = Number(value.replace("item-", ""));
	return Number.isInteger(index) ? index : null;
}

export function Faq() {
	const { capture } = useAnalytics();

	return (
		<section
			className="w-full border-t border-border py-12 md:py-24 lg:py-32"
			id="faq"
		>
			<div className="container mx-auto px-4 md:px-6">
				<div className="flex flex-col items-center justify-center space-y-4 text-center">
					<div className="space-y-2">
						<p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
							FAQ
						</p>
						<h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
							Questions teams ask before they commit
						</h2>
						<p className="mx-auto max-w-[700px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
							How agents run, where your data lives, and what it costs.
						</p>
					</div>
				</div>

				<div className="mx-auto mt-8 max-w-3xl">
					<Accordion
						multiple={false}
						className="w-full"
						defaultValue={["item-0"]}
						onValueChange={(value: unknown[]) => {
							// Only the newly opened item is reported; collapsing emits nothing.
							const openedIndex = value
								.map((entry) => faqIndexFromValue(entry))
								.find((index) => index !== null);
							if (openedIndex === undefined || openedIndex === null) {
								return;
							}
							const item = faqItems[openedIndex];
							if (!item) {
								return;
							}
							capture("faq_expanded", {
								question: item.question,
								position: openedIndex + 1,
							});
						}}
					>
						{faqItems.map((item, i) => (
							<AccordionItem
								key={item.question}
								value={`item-${String(i)}`}
								className="py-2"
							>
								<AccordionTrigger className="text-lg font-medium leading-6 hover:no-underline">
									{item.question}
								</AccordionTrigger>
								<AccordionContent className="leading-relaxed text-muted-foreground">
									{item.answer}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
				</div>
			</div>
		</section>
	);
}
